import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Upload, Camera, Check, X, FileText, User, 
  AlertCircle, Clock, Car, Bike, Truck, Container
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const documentTypes = [
  { id: 'cc', name: 'Cartão de Cidadão', description: 'Documento oficial português' },
  { id: 'bi', name: 'Bilhete de Identidade', description: 'Documento de identificação' },
  { id: 'passport', name: 'Passaporte', description: 'Documento de viagem' }
];

const vehicleTypes = [
  { id: 'mota', name: 'Mota/Scooter', icon: Bike, description: 'Entregas pequenas e rápidas' },
  { id: 'carro', name: 'Carro', icon: Car, description: 'Entregas médias' },
  { id: 'carrinha', name: 'Carrinha', icon: Truck, description: 'Volumes grandes' },
  { id: 'camiao', name: 'Camião', icon: Container, description: 'Mudanças e cargas pesadas' }
];

export const DriverVerification = () => {
  const [step, setStep] = useState(1);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [formData, setFormData] = useState({
    id_document_type: '',
    id_front: null,
    id_back: null,
    selfie: null,
    vehicle_type: '',
    vehicle_plate: '',
    vehicle_doc: null,
    vehicle_insurance: null
  });
  const [previews, setPreviews] = useState({
    id_front: null,
    id_back: null,
    selfie: null,
    vehicle_doc: null,
    vehicle_insurance: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const fileInputIdFront = useRef(null);
  const fileInputIdBack = useRef(null);
  const fileInputSelfie = useRef(null);
  const fileInputVehicleDoc = useRef(null);
  const fileInputInsurance = useRef(null);
  
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchVerificationStatus();
  }, []);

  const fetchVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API}/verification/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationStatus(response.data);
      
      if (response.data.is_verified) {
        navigate('/driver');
      }
    } catch (error) {
      console.error('Error fetching verification status:', error);
    }
  };

  const handleFileChange = async (type, event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviews(prev => ({ ...prev, [type]: reader.result }));
      setFormData(prev => ({ ...prev, [type]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const uploadDocument = async (type, base64Data) => {
    try {
      await axios.post(`${API}/verification/upload-document`, {
        document_type: type,
        document_data: base64Data
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return true;
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      return false;
    }
  };

  const handleSubmitVerification = async () => {
    setError('');
    setLoading(true);

    try {
      // Upload all documents
      if (formData.id_front) await uploadDocument('id_front', formData.id_front);
      if (formData.id_back) await uploadDocument('id_back', formData.id_back);
      if (formData.selfie) await uploadDocument('selfie', formData.selfie);
      if (formData.vehicle_doc) await uploadDocument('vehicle_doc', formData.vehicle_doc);
      if (formData.vehicle_insurance) await uploadDocument('vehicle_insurance', formData.vehicle_insurance);

      // Submit verification
      await axios.post(`${API}/verification/driver/submit`, {
        id_document_type: formData.id_document_type,
        vehicle_type: formData.vehicle_type,
        vehicle_plate: formData.vehicle_plate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStep(5); // Success step
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao submeter documentos');
    } finally {
      setLoading(false);
    }
  };

  // Show status screens
  if (verificationStatus?.verification_status === 'submitted') {
    return (
      <div className="min-h-screen bg-gray-100 py-8 px-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-3xl p-8 shadow-xl text-center">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-yellow-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verificação em Análise</h1>
            <p className="text-gray-600 mb-6">
              Os seus documentos estão a ser analisados pela nossa equipa. Este processo pode demorar até 24 horas.
            </p>
            <Link to="/driver">
              <Button variant="outline" className="rounded-full">
                Voltar ao Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus?.verification_status === 'rejected') {
    return (
      <div className="min-h-screen bg-gray-100 py-8 px-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-3xl p-8 shadow-xl text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <X className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verificação Rejeitada</h1>
            <p className="text-gray-600 mb-4">
              {verificationStatus.verification_details?.rejection_reason || 'Os seus documentos não foram aprovados. Por favor, tente novamente.'}
            </p>
            <Button 
              onClick={() => {
                setVerificationStatus(null);
                setStep(1);
              }}
              className="bg-tuggo-mint text-white rounded-full"
            >
              Tentar Novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-md mx-auto">
        <Link to="/driver" className="inline-flex items-center gap-2 text-gray-600 hover:text-tuggo-mint mb-6">
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="bg-white rounded-3xl p-8 shadow-xl">
          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= s ? 'bg-tuggo-mint text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 4 && <div className={`w-6 h-1 mx-1 ${step > s ? 'bg-tuggo-mint' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          {/* Step 1: Document Type */}
          {step === 1 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-tuggo-mint/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-tuggo-mint" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Documento de Identidade</h1>
                <p className="text-gray-600">Selecione o tipo de documento</p>
              </div>

              <div className="space-y-3">
                {documentTypes.map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => setFormData({ ...formData, id_document_type: doc.id })}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      formData.id_document_type === doc.id
                        ? 'border-tuggo-mint bg-tuggo-mint/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">{doc.name}</div>
                    <div className="text-sm text-gray-500">{doc.description}</div>
                  </button>
                ))}
              </div>

              <Button
                onClick={() => setStep(2)}
                disabled={!formData.id_document_type}
                className="w-full mt-6 bg-tuggo-mint text-white rounded-full py-6"
              >
                Continuar
              </Button>
            </div>
          )}

          {/* Step 2: ID Photos + Selfie */}
          {step === 2 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-tuggo-mint/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Camera className="w-8 h-8 text-tuggo-mint" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Fotografias</h1>
                <p className="text-gray-600">Documento e selfie para verificação</p>
              </div>

              {/* ID Front */}
              <div className="mb-4">
                <Label className="block mb-2">Frente do Documento</Label>
                <input type="file" ref={fileInputIdFront} onChange={(e) => handleFileChange('id_front', e)} accept="image/*" capture="environment" className="hidden" />
                <button
                  onClick={() => fileInputIdFront.current?.click()}
                  className={`w-full h-32 rounded-xl border-2 border-dashed flex flex-col items-center justify-center ${
                    previews.id_front ? 'border-tuggo-mint' : 'border-gray-300 hover:border-tuggo-mint'
                  }`}
                >
                  {previews.id_front ? (
                    <img src={previews.id_front} alt="Frente" className="h-full w-full object-cover rounded-lg" />
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-gray-400 mb-1" />
                      <span className="text-sm text-gray-500">Clique para fotografar</span>
                    </>
                  )}
                </button>
              </div>

              {/* ID Back */}
              <div className="mb-4">
                <Label className="block mb-2">Verso do Documento</Label>
                <input type="file" ref={fileInputIdBack} onChange={(e) => handleFileChange('id_back', e)} accept="image/*" capture="environment" className="hidden" />
                <button
                  onClick={() => fileInputIdBack.current?.click()}
                  className={`w-full h-32 rounded-xl border-2 border-dashed flex flex-col items-center justify-center ${
                    previews.id_back ? 'border-tuggo-mint' : 'border-gray-300 hover:border-tuggo-mint'
                  }`}
                >
                  {previews.id_back ? (
                    <img src={previews.id_back} alt="Verso" className="h-full w-full object-cover rounded-lg" />
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-gray-400 mb-1" />
                      <span className="text-sm text-gray-500">Clique para fotografar</span>
                    </>
                  )}
                </button>
              </div>

              {/* Selfie */}
              <div className="mb-6">
                <Label className="block mb-2">Selfie (Foto do Rosto)</Label>
                <input type="file" ref={fileInputSelfie} onChange={(e) => handleFileChange('selfie', e)} accept="image/*" capture="user" className="hidden" />
                <button
                  onClick={() => fileInputSelfie.current?.click()}
                  className={`w-full h-40 rounded-xl border-2 border-dashed flex flex-col items-center justify-center ${
                    previews.selfie ? 'border-tuggo-mint' : 'border-gray-300 hover:border-tuggo-mint'
                  }`}
                >
                  {previews.selfie ? (
                    <img src={previews.selfie} alt="Selfie" className="h-full w-full object-cover rounded-lg" />
                  ) : (
                    <>
                      <User className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Tire uma selfie bem iluminada</span>
                    </>
                  )}
                </button>
              </div>

              <div className="flex gap-4">
                <Button onClick={() => setStep(1)} variant="outline" className="flex-1 rounded-full py-6">
                  Voltar
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!previews.id_front || !previews.id_back || !previews.selfie}
                  className="flex-1 bg-tuggo-mint text-white rounded-full py-6"
                >
                  Continuar
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Vehicle Info */}
          {step === 3 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-tuggo-mint/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Car className="w-8 h-8 text-tuggo-mint" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Veículo</h1>
                <p className="text-gray-600">Informações do seu veículo</p>
              </div>

              <div className="mb-4">
                <Label className="block mb-2">Tipo de Veículo</Label>
                <div className="grid grid-cols-2 gap-3">
                  {vehicleTypes.map((vehicle) => {
                    const Icon = vehicle.icon;
                    return (
                      <button
                        key={vehicle.id}
                        onClick={() => setFormData({ ...formData, vehicle_type: vehicle.id })}
                        className={`p-4 rounded-xl border-2 text-left ${
                          formData.vehicle_type === vehicle.id
                            ? 'border-tuggo-mint bg-tuggo-mint/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className={`w-6 h-6 mb-2 ${formData.vehicle_type === vehicle.id ? 'text-tuggo-mint' : 'text-gray-400'}`} />
                        <div className="font-semibold text-gray-900 text-sm">{vehicle.name}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mb-6">
                <Label>Matrícula do Veículo</Label>
                <Input
                  value={formData.vehicle_plate}
                  onChange={(e) => setFormData({ ...formData, vehicle_plate: e.target.value.toUpperCase() })}
                  placeholder="AA-00-AA"
                  className="mt-2"
                />
              </div>

              <div className="flex gap-4">
                <Button onClick={() => setStep(2)} variant="outline" className="flex-1 rounded-full py-6">
                  Voltar
                </Button>
                <Button
                  onClick={() => setStep(4)}
                  disabled={!formData.vehicle_type || !formData.vehicle_plate}
                  className="flex-1 bg-tuggo-mint text-white rounded-full py-6"
                >
                  Continuar
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Vehicle Documents */}
          {step === 4 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-tuggo-mint/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-tuggo-mint" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Documentos do Veículo</h1>
                <p className="text-gray-600">Carta de condução e seguro</p>
              </div>

              {/* Carta de Condução */}
              <div className="mb-4">
                <Label className="block mb-2">Carta de Condução</Label>
                <input type="file" ref={fileInputVehicleDoc} onChange={(e) => handleFileChange('vehicle_doc', e)} accept="image/*" capture="environment" className="hidden" />
                <button
                  onClick={() => fileInputVehicleDoc.current?.click()}
                  className={`w-full h-32 rounded-xl border-2 border-dashed flex flex-col items-center justify-center ${
                    previews.vehicle_doc ? 'border-tuggo-mint' : 'border-gray-300 hover:border-tuggo-mint'
                  }`}
                >
                  {previews.vehicle_doc ? (
                    <img src={previews.vehicle_doc} alt="Carta" className="h-full w-full object-cover rounded-lg" />
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-gray-400 mb-1" />
                      <span className="text-sm text-gray-500">Fotografar carta de condução</span>
                    </>
                  )}
                </button>
              </div>

              {/* Seguro */}
              <div className="mb-6">
                <Label className="block mb-2">Seguro do Veículo</Label>
                <input type="file" ref={fileInputInsurance} onChange={(e) => handleFileChange('vehicle_insurance', e)} accept="image/*" capture="environment" className="hidden" />
                <button
                  onClick={() => fileInputInsurance.current?.click()}
                  className={`w-full h-32 rounded-xl border-2 border-dashed flex flex-col items-center justify-center ${
                    previews.vehicle_insurance ? 'border-tuggo-mint' : 'border-gray-300 hover:border-tuggo-mint'
                  }`}
                >
                  {previews.vehicle_insurance ? (
                    <img src={previews.vehicle_insurance} alt="Seguro" className="h-full w-full object-cover rounded-lg" />
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-gray-400 mb-1" />
                      <span className="text-sm text-gray-500">Fotografar comprovativo de seguro</span>
                    </>
                  )}
                </button>
              </div>

              <div className="flex gap-4">
                <Button onClick={() => setStep(3)} variant="outline" className="flex-1 rounded-full py-6">
                  Voltar
                </Button>
                <Button
                  onClick={handleSubmitVerification}
                  disabled={!previews.vehicle_doc || !previews.vehicle_insurance || loading}
                  className="flex-1 bg-tuggo-mint text-white rounded-full py-6"
                >
                  {loading ? 'A enviar...' : 'Submeter'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Success */}
          {step === 5 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Documentos Enviados!</h1>
              <p className="text-gray-600 mb-6">
                A sua verificação está em análise. Receberá uma notificação quando for aprovada.
              </p>
              <Link to="/driver">
                <Button className="bg-tuggo-mint text-white rounded-full px-8">
                  Voltar ao Dashboard
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
